import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import AdminService from '../../services/adminService';

// Sub-components
import StatCard from '../../components/admin/control_room/StatCard';
import CriticalAlerts from '../../components/admin/control_room/CriticalAlerts';
import StagnantCases from '../../components/admin/control_room/StagnantCases';
import UpcomingDeadlines from '../../components/admin/control_room/UpcomingDeadlines';
import DeadlineTimeline from '../../components/admin/control_room/DeadlineTimeline';
import TeamWorkload from '../../components/admin/control_room/TeamWorkload';
import ActivityFeed from '../../components/admin/control_room/ActivityFeed';
import QuickActions from '../../components/admin/control_room/QuickActions';
import SystemHealth from '../../components/admin/control_room/SystemHealth';

const AdminControlRoom = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showAcknowledged, setShowAcknowledged] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const [stats, setStats] = useState({
        activeCases: 0,
        pendingTasks: 0,
        urgentDeadlines: 0,
        overdueCount: 0,
        completedToday: 0,
        clientGaps: 0,
        criticalAlerts: [],
        allDeadlines: [],
        upcomingDeadlines: [],
        openUniApplications: [],
        stagnantCases: [],
        workload: [],
        recentActivity: []
    });

    const fetchDashboardData = useCallback(async (isRefresh = false) => {
        try {
            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            console.log('Fetching Control Room Data...');

            // Parallelize independent fetches using allSettled to prevent one failure from blocking everything
            const results = await Promise.allSettled([
                // 0. Active Cases Count
                supabase
                    .from('cases')
                    .select('*', { count: 'exact', head: true })
                    .neq('status', 'closed')
                    .neq('status', 'archived'),

                // 1. Pending Tasks Count
                supabase
                    .from('case_tasks')
                    .select('*', { count: 'exact', head: true })
                    .neq('status', 'completed'),

                // 2. Unified Deadlines (Critical & Upcoming)
                (async () => {
                    try {
                        const now = new Date();
                        const fourteenDaysFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
                        return await AdminService.getUnifiedDeadlines({
                            startDate: now.toISOString().split('T')[0],
                            endDate: fourteenDaysFromNow.toISOString().split('T')[0],
                            alertLevels: ['critical', 'urgent', 'attention', 'normal'],
                            deadlineTypes: ['task', 'university_application', 'visa_application', 'agreement', 'case'],
                            includeAcknowledged: showAcknowledged
                        });
                    } catch (e) {
                        console.error("Error fetching unified deadlines:", e);
                        return { data: [], error: e };
                    }
                })(),

                // 3. Stagnant Cases
                (async () => {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    return await supabase
                        .from('cases')
                        .select('id, title, status, updated_at, assigned_to')
                        .neq('status', 'closed')
                        .neq('status', 'archived')
                        .lt('updated_at', sevenDaysAgo.toISOString())
                        .limit(5);
                })(),

                // 4. Activity Feed
                supabase
                    .from('audit_logs')
                    .select('*')
                    .order('created_at', { ascending: false })
                    .limit(20)
            ]);

            // Helper to get success value or default
            const getResult = (index, defaultVal) => {
                if (results[index].status === 'fulfilled') {
                    if (results[index].value.error) {
                        console.error(`Fetch error at index ${index}:`, results[index].value.error);
                        return defaultVal;
                    }
                    return results[index].value;
                }
                console.error(`Promise rejected at index ${index}:`, results[index].reason);
                return defaultVal;
            };

            const casesRes = getResult(0, { count: 0 });
            const tasksRes = getResult(1, { count: 0 });
            const deadlinesRes = getResult(2, { data: [] });
            const stagnantRes = getResult(3, { data: [] });
            const activityRes = getResult(4, { data: [] });

            // --- Process Deadlines ---
            const unifiedInfo = deadlinesRes.data || [];
            const now = new Date();
            const twoDaysFromNow = new Date(now.getTime() + 48 * 60 * 60 * 1000);
            const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            const fourteenDaysFromNow = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

            const unified = unifiedInfo
                .filter(d => d.deadline)
                .map(d => {
                    const date = new Date(d.deadline);

                    let type = d.deadline_type;
                    if (type === 'university_application') type = 'application';

                    let subtitle = '';
                    if (d.deadline_type === 'task') {
                        subtitle = d.case_title || 'Case Task';
                    } else if (d.deadline_type === 'university_application') {
                        subtitle = 'Uni Application';
                    } else if (d.deadline_type === 'visa_application') {
                        subtitle = 'Visa Application';
                    } else if (d.deadline_type === 'agreement') {
                        subtitle = 'Agreement';
                    } else if (d.deadline_type === 'case') {
                        subtitle = 'Case Deadline';
                    }

                    return {
                        id: d.id,
                        type,
                        title: d.title,
                        subtitle,
                        date,
                        status: d.status,
                        source: d.source_table,
                        alertLevel: d.alert_level,
                        overdue: d.overdue,
                        programId: d.program_id,
                        priority: d.priority
                    };
                })
                .sort((a, b) => a.date - b.date);

            // Fetch all open university applications (not submitted), including ones without deadlines
            const { data: uniApps, error: uniError } = await supabase
                .from('uni_applications')
                .select(`
                    id,
                    status,
                    metadata,
                    case_id,
                    programs (
                        name,
                        university: universities ( name )
                    )
                `)
                .neq('status', 'submitted');

            if (uniError) {
                console.error('Error fetching open university applications for control room:', uniError);
            }

            const openUniApplications = (uniApps || []).map(app => {
                const deadlineStr = app.metadata?.deadline || null;
                const deadline = deadlineStr ? new Date(deadlineStr) : null;

                const university =
                    app.programs?.university?.name ||
                    app.metadata?.university ||
                    null;
                const program =
                    app.programs?.name ||
                    app.metadata?.program_text ||
                    null;

                return {
                    id: app.id,
                    title: university && program ? `${university} - ${program}` : (program || university || 'University Application'),
                    status: app.status,
                    deadline,
                    university,
                    program
                };
            }).sort((a, b) => {
                if (!a.deadline && !b.deadline) return 0;
                if (!a.deadline) return 1;
                if (!b.deadline) return -1;
                return a.deadline - b.deadline;
            });

            // --- MERGE UNI APPS INTO UNIFIED ---
            // Convert openUniApplications to unified format and merge with existing unified data
            const uniAppsForUnified = openUniApplications.map(app => {
                const now = new Date();
                const hoursUntil = app.deadline ? (app.deadline.getTime() - now.getTime()) / (1000 * 60 * 60) : Number.POSITIVE_INFINITY;
                let alertLevel = 'normal';
                if (hoursUntil <= 24) alertLevel = 'critical';
                else if (hoursUntil <= 72) alertLevel = 'urgent';
                else if (hoursUntil <= 168) alertLevel = 'attention';

                const isOverdue = app.deadline && app.deadline < now;

                return {
                    id: app.id,
                    type: 'application',
                    title: app.title,
                    subtitle: `${app.status || 'draft'} • Uni Application`,
                    date: app.deadline,
                    status: app.status,
                    source: 'uni_applications',
                    alertLevel: isOverdue ? 'critical' : alertLevel,
                    overdue: isOverdue,
                    programId: null,
                    priority: null
                };
            });

            // Merge: add uni apps that aren't already in unified (by ID)
            const unifiedIds = new Set(unified.map(d => d.id));
            const mergedUnified = [
                ...unified,
                ...uniAppsForUnified.filter(u => !unifiedIds.has(u.id))
            ].sort((a, b) => {
                if (!a.date && !b.date) return 0;
                if (!a.date) return 1;
                if (!b.date) return -1;
                return a.date - b.date;
            });

            // --- FILTERING LOGIC AS PER USER REQUEST ---
            // Critical Alerts:
            // 1. Overdue items (Immediate attention)
            // 2. Critical Tasks (Priority = High/Critical) regardless of date? Or just close ones? Assuming due within 7 days for visibility.
            // 3. University Deadlines (All due within 7 days)
            // 4. Any other item due within 48 hours (Urgent)

            const criticalAlerts = mergedUnified.filter(d => {
                // Always show overdue
                if (d.overdue) return true;

                // Show anything due extremely soon (48h)
                if (d.date <= twoDaysFromNow) return true;

                // Specific Types prioritization (Due within 14 days window fetched)
                // User said: "Critical Alerts should have critical tasks and university deadlines"

                // University Applications due soon (e.g., < 7 days)
                if ((d.type === 'application' || d.type === 'university_application') && d.date <= sevenDaysFromNow) return true;

                // Tasks that are critical/high priority (and due relative soon, e.g. < 7 days)
                // Note: d.priority might need to be separately fetched if not in unified view. 
                // For now, valid assumption is 'alertLevel' from service handles priority.
                if (d.type === 'task' && d.alertLevel === 'critical') return true;

                return false;
            });

            // Upcoming Deadlines:
            // "Upcoming deadline in uni application, task, visa applications"
            // Filter strictly for these types, in the future, not already in Critical (optional, but duplicate is usually bad)
            // Let's show next 7-14 days.

            const upcomingDeadlines = mergedUnified.filter(d => {
                if (d.overdue) return false; // Overdue goes to Critical
                if (d.date <= new Date()) return false; // Already passed or today (covered by critical/overdue logic often)

                // Exclude if already in critical? 
                // User might want to see them in Upcoming list too if they are just "upcoming". 
                // Usually "Upcoming" implies "Soon but not Panic".
                // Let's exclude "Immediate" items (< 48h) from Upcoming to define "Upcoming" as "Soon but not Panic".
                if (d.date <= twoDaysFromNow) return false;

                const allowedTypes = ['task', 'application', 'university_application', 'visa_application', 'visa'];
                return allowedTypes.includes(d.type);
            }).slice(0, 10);

            // Recalculate urgent count based on merged data (e.g. < 3 days)
            const urgentCount = mergedUnified.filter(d => d.date <= new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000) && !d.overdue).length;

            const { data: highPriorityCases } = await supabase
                .from('cases')
                .select('id, title, status, priority, due_date')
                .eq('priority', 'high')
                .neq('status', 'closed');

            const criticalCasesMapped = (highPriorityCases || []).map(c => ({
                id: c.id,
                type: 'case',
                title: c.title,
                subtitle: 'High Priority Case',
                date: c.due_date ? new Date(c.due_date) : null,
                status: c.status,
                source: 'cases'
            }));

            // Combine for display (Merges with High Priority Cases that might not be in unified view if not a deadline per se)
            // But Unified View usually covers deadlines. If Case has due_date, it should be in unified.
            // Let's just append specific high priority cases if they are NOT in the list already.
            const combinedCritical = [
                ...criticalAlerts,
                ...criticalCasesMapped.filter(c => !criticalAlerts.find(d => d.id === c.id && d.source === 'cases'))
            ].sort((a, b) => (a.date || 0) - (b.date || 0)).slice(0, 20); // Check dates

            // --- Workload Calculation (requires separate fetch or derivation) ---
            // We can fetch active tasks and group by user
            const { data: allActiveTasks } = await supabase
                .from('case_tasks')
                .select('assigned_to')
                .neq('status', 'completed');

            const workloadMap = {};
            (allActiveTasks || []).forEach(task => {
                const assignee = task.assigned_to || 'Unassigned';
                workloadMap[assignee] = (workloadMap[assignee] || 0) + 1;
            });
            const workload = Object.entries(workloadMap)
                .map(([name, count]) => ({ name, count }))
                .sort((a, b) => b.count - a.count)
                .slice(0, 5);


            // Calculate overdue count (from merged data including uni apps)
            const overdueCount = mergedUnified.filter(d => d.overdue || (d.date && d.date < new Date())).length;

            // Calculate completed today (from activity feed)
            const todayStart = new Date();
            todayStart.setHours(0, 0, 0, 0);
            const completedToday = (activityRes.data || []).filter(a =>
                a.action?.includes('completed') && new Date(a.created_at) >= todayStart
            ).length;

            // Client gaps count fallback (using stagnant cases as indicator)
            const clientGapsCount = stagnantRes.data?.length || 0;

            setStats({
                activeCases: casesRes.count || 0,
                pendingTasks: tasksRes.count || 0,
                urgentDeadlines: urgentCount,
                overdueCount,
                completedToday,
                clientGaps: clientGapsCount || stagnantRes.data?.length || 0,
                criticalAlerts: combinedCritical,
                allDeadlines: mergedUnified,
                upcomingDeadlines,
                stagnantCases: stagnantRes.data || [],
                workload,
                recentActivity: activityRes.data || [],
                openUniApplications: []
            });
            setLastUpdated(new Date());

        } catch (error) {
            console.error('Error fetching control room data:', error);
            // Optionally set error state here to show user
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [showAcknowledged]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const normalizeDeadlineType = (alertType) => {
        if (alertType === 'application') return 'university_application';
        return alertType;
    };

    const handleAlertAcknowledge = async (alert) => {
        try {
            await AdminService.acknowledgeDeadlineAlert(normalizeDeadlineType(alert.type), alert.id, {
                source: 'control_room_v2'
            });
            // Optimistic update or refetch
            fetchDashboardData(true);
        } catch (e) {
            console.error('Failed to acknowledge', e);
        }
    };

    const handleAcknowledgeAll = async () => {
        if (!stats.criticalAlerts || !stats.criticalAlerts.length) return;
        // Implementation for bulk ack
        try {
            await Promise.all(stats.criticalAlerts.map(alert =>
                AdminService.acknowledgeDeadlineAlert(normalizeDeadlineType(alert.type), alert.id, { source: 'bulk_v2' })
            ));
            fetchDashboardData(true);
        } catch (e) {
            console.error('Bulk ack failed', e);
        }
    };

    const handleNavigation = (item) => {
        const base = '/admin';
        if (!item || !item.type) return;

        switch (item.type) {
            case 'task': navigate(`${base}/tasks/${item.id}`); break;
            case 'university_application':
            case 'application':
                if (item.programId) navigate(`${base}/programs/${item.programId}/cases`);
                else navigate(`${base}/univ_apps/${item.id}`);
                break;
            case 'visa':
            case 'visa_application': navigate(`${base}/visa_apps/${item.id}`); break;
            case 'agreement': navigate(`${base}/agreements/${item.id}`); break;
            case 'case': navigate(`${base}/cases/${item.id}`); break;
            default: break;
        }
    };

    return (
        <div className="p-4 lg:p-6 bg-gray-900 text-white min-h-screen font-mono">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 border-b border-gray-700 pb-4 gap-4">
                <div>
                    <h1 className="text-3xl font-bold tracking-wider text-blue-400 uppercase">Control Room</h1>
                    <div className="flex items-center gap-2 mt-1">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                        </span>
                        <p className="text-gray-400 text-sm">System Online</p>
                        <span className="text-gray-600 mx-2">|</span>
                        <p className="text-gray-500 text-sm">Updated: {lastUpdated.toLocaleTimeString()}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => fetchDashboardData(true)}
                        disabled={refreshing}
                        className={`px-4 py-2 bg-gray-800 border border-gray-600 rounded hover:bg-gray-700 transition-colors flex items-center gap-2 text-sm ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        {refreshing ? 'Refreshing...' : 'Refresh Data'}
                    </button>
                </div>
            </div>

            {/* Top Stats Row - 6 cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                <StatCard
                    title="Active Cases"
                    value={stats.activeCases}
                    color="blue"
                    icon={<svg className="w-16 h-16 text-blue-500" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" /><path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" /></svg>}
                    loading={loading}
                    onClick={() => navigate('/admin/cases')}
                />
                <StatCard
                    title="Pending Tasks"
                    value={stats.pendingTasks}
                    color="yellow"
                    icon={<svg className="w-16 h-16 text-yellow-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
                    loading={loading}
                    onClick={() => navigate('/admin/tasks')}
                />
                <StatCard
                    title="Urgent (72h)"
                    value={stats.urgentDeadlines}
                    color="orange"
                    icon={<svg className="w-16 h-16 text-orange-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>}
                    loading={loading}
                />
                <StatCard
                    title="Overdue"
                    value={stats.overdueCount}
                    color="red"
                    pulse={stats.overdueCount > 0}
                    icon={<svg className="w-16 h-16 text-red-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>}
                    loading={loading}
                />
                <StatCard
                    title="Completed Today"
                    value={stats.completedToday}
                    color="green"
                    icon={<svg className="w-16 h-16 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>}
                    loading={loading}
                />
                <StatCard
                    title="Stagnant Cases"
                    value={stats.stagnantCases?.length || 0}
                    color="purple"
                    subtitle="> 7 days no activity"
                    icon={<svg className="w-16 h-16 text-purple-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" /></svg>}
                    loading={loading}
                    onClick={() => navigate('/admin/cases?filter=stagnant')}
                />
            </div>

            {/* Quick Actions & System Health Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="md:col-span-2">
                    <QuickActions />
                </div>
                <div>
                    <SystemHealth />
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                {/* Left Column: Alerts & Deadlines */}
                <div className="space-y-8 lg:col-span-2">
                    {/* Critical Alerts */}
                    <div className="min-h-[300px]">
                        <CriticalAlerts
                            alerts={stats.criticalAlerts}
                            loading={loading}
                            showAcknowledged={showAcknowledged}
                            onToggleAcknowledged={() => setShowAcknowledged(!showAcknowledged)}
                            onAcknowledge={handleAlertAcknowledge}
                            onAcknowledgeAll={handleAcknowledgeAll}
                            onView={handleNavigation}
                        />
                    </div>

                    {/* Stagnant Cases */}
                    <div>
                        <StagnantCases cases={stats.stagnantCases} loading={loading} />
                    </div>

                    {/* Deadline Timeline */}
                    <div className="min-h-[400px]">
                        <DeadlineTimeline
                            deadlines={stats.allDeadlines}
                            loading={loading}
                            onItemClick={handleNavigation}
                        />
                    </div>
                </div>

                {/* Right Column: Workload & Activity */}
                <div className="space-y-8">
                    {/* Team Workload */}
                    <div className="min-h-[300px]">
                        <TeamWorkload workload={stats.workload} loading={loading} />
                    </div>

                    {/* Activity Feed */}
                    <div className="h-[500px]">
                        <ActivityFeed activities={stats.recentActivity} loading={loading} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminControlRoom;
